import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { fmtDate } from "../lib/format";
import { Megaphone, Send, Trash2 } from "lucide-react";

export default function Announcements() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["announcements"], queryFn: () => api.get("/announcements").then((r) => r.data) });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => api.get("/companies").then((r) => r.data) });
  const companyMap = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [units, setUnits] = useState([]);
  const [posting, setPosting] = useState(false);
  const toggleUnit = (id) => setUnits((u) => (u.includes(id) ? u.filter((x) => x !== id) : [...u, id]));

  const post = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title and message are required."); return; }
    setPosting(true);
    try {
      await api.post("/announcements", { title: title.trim(), message: message.trim(), company_ids: units });
      await qc.invalidateQueries({ queryKey: ["announcements"] });
      setTitle(""); setMessage(""); setUnits([]);
      toast.success("Announcement posted — staff will see it on their dashboard.");
    } catch (e) {
      toast.error("Could not post announcement.");
    } finally { setPosting(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      await qc.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Announcement removed.");
    } catch (e) { toast.error("Could not remove announcement."); }
  };

  return (
    <div>
      <PageHeader title="Announcements" subtitle="Post updates your team sees the moment they log in" />

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm mb-6" data-testid="announcement-composer">
        <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-slate-700"><Megaphone className="h-4 w-4" /> New announcement</div>
        <div className="space-y-3">
          <input
            data-testid="announcement-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full h-11 px-3.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
          />
          <textarea
            data-testid="announcement-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message to the team…"
            rows={4}
            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] resize-y"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="announcement-units">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Send to:</span>
              <button type="button" onClick={() => setUnits([])} data-testid="announcement-unit-all"
                className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${units.length === 0 ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                All units
              </button>
              {companies.map((c) => (
                <button type="button" key={c.id} onClick={() => toggleUnit(c.id)} data-testid={`announcement-unit-${c.id}`}
                  className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${units.includes(c.id) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <button
              data-testid="announcement-post-btn"
              onClick={post}
              disabled={posting}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium shadow-sm hover:bg-[#152C6F] transition-colors disabled:opacity-60">
              <Send className="h-4 w-4" /> {posting ? "Posting…" : "Post announcement"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">Posted announcements</div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState text="No announcements posted yet." />
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((a) => (
              <div key={a.id} data-testid={`announcement-row-${a.id}`} className="flex items-start justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-slate-800">{a.title}</div>
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap break-words">{a.message}</div>
                  <div className="text-xs text-slate-400 mt-2">{a.author ? `${a.author} · ` : ""}{fmtDate(a.created_at)}</div>
                  <div className="text-xs text-slate-500 mt-1" data-testid={`announcement-target-${a.id}`}>
                    To: {(a.company_ids || []).length === 0 ? "All units" : a.company_ids.map((c) => companyMap[c] || "—").join(", ")}
                  </div>
                  <div className="text-xs text-slate-500 mt-2" data-testid={`announcement-seen-${a.id}`}>
                    {(a.reads || []).length === 0 ? "Not seen yet" : `Seen by ${a.reads.length}: ${a.reads.map((r) => r.name).filter(Boolean).join(", ")}`}
                  </div>
                </div>
                <Confirm
                  onConfirm={() => remove(a.id)}
                  title="Delete announcement?"
                  description="This will remove it from everyone's dashboard."
                  trigger={
                    <button data-testid={`announcement-delete-${a.id}`} className="shrink-0 h-9 w-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
